<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="systems-page-title" class="mb-1">
      <button class="btn btn-secondary-dark float-right"
              @click="handleFilterButton(false)"
              qid="systems-filter-button"
      >
        <font-awesome-icon icon="filter"/>
      </button>
      <button class="btn btn-secondary-dark float-right"
              @click="handleDownloadModal"
              qid="download-table-button"
              v-if="isGranted(permissions.MAINTENANCE_VIEW)"
      >
        <font-awesome-icon icon="file-download"/>
      </button>
      <router-link
          to="/systems/create"
          class="btn btn-success float-right"
          qid="create-s-button"
          v-if="isGranted(permissions.SYSTEM_CREATE)"
      >
        <font-awesome-icon icon="plus-circle"/> {{ $t('systems.create_button') }}
      </router-link>
      <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-s-input"
          v-on:input="debounceInput"
          :placeholder="$t('systems.search_placeholder')"
      ></b-form-input>
      {{ $t('systems.title') }}
    </h4>
    <div>
      <span v-if="getGuide('system_list_page')">
        <GuideButton :guide="getGuide('system_list_page')"></GuideButton>
      </span>
    </div>

    <div class="row mt-4" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="">
          <table class="table table-responsive-md table-list">
            <thead>
              <tr>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('name')"
                    qid="sort-s-name"
                >
                  {{ $t('systems.table.name') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                  </span>
                </th>
                <th scope="col" v-if="tableColumns.responsibles">
                  {{ $t('systems.table.owners') }}
                </th>
                <th scope="col" v-if="tableColumns.tags">
                  {{ $t('systems.table.tags') }}
                </th>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('updated_at')"
                    qid="sort-s-name"
                >
                  {{ $t('system.last_updated') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                  </span>
                </th>
                <th width="180" class="text-right">
                  <b-nav-item-dropdown right extra-toggle-classes="no-dropdown-caret" class="guide-settings">
                    <!-- Using 'button-content' slot -->
                    <template slot="button-content">
                      <span class="float-right" style="list-style: none;">
                        <font-awesome-icon size="lg" icon="cog" class="text-muted"/>
                      </span>
                    </template>

                    <div class="pl-4 pr-4">
                      <b-form-checkbox
                        v-model="tableColumns.responsibles"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('systems.table.owners') }}
                      </b-form-checkbox>
                      <br>
                      <b-form-checkbox
                        v-model="tableColumns.tags"
                        name="checkbox-disable"
                        :value="1"
                        :unchecked-value="0"
                      >
                        {{ $t('systems.table.tags') }}
                      </b-form-checkbox>
                      <br>
                    </div>
                    <hr>
                    <div class="pl-4 pr-4">
                      <small class="text-muted">
                        * {{ $t('system.global_table_expand_explainer_text') }}
                      </small>
                    </div>
                  </b-nav-item-dropdown>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'systems.view', params: { slug: object.slug }}"
                      qid="s-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td v-if="tableColumns.responsibles">
                  <span class="mr-1 text-normal" v-for="user in getReducedUsers(object.users)">
                    <span qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
                  <span v-if="object.users.length > 3"
                        class="badge cursor-pointer"
                        @click="openUsersModal(object)"
                  >
                    <font-awesome-icon icon="users"/>
                    + {{ object.users.length - 3 }}
                  </span>
                </td>
                <td v-if="tableColumns.tags">
                  <span v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                    <span class="badge" :title="tag.name_translated">
                      <font-awesome-icon icon="tag"/>
                      {{ tag.name_translated | truncate(35) }}
                    </span>
                  </span>
                  <span v-if="object.tags_translated.length > 3"
                        class="badge cursor-pointer"
                        @click="openTagsModal(object)"
                  >
                    <font-awesome-icon icon="tags"/>
                    + {{ object.tags_translated.length - 3 }}
                  </span>
                </td>
                <td>
                  <span class="text-normal">
                    {{ object.updated_at | formatDate }}
                  </span>
                </td>
                <td align="right">
                  <SystemEditForm
                      :object="object"
                      @reloadContent="loadContent(currentPage)"
                      v-if="isGranted([permissions.SYSTEM_EDIT, permissions.SYSTEM_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                  ></SystemEditForm>
                  <span class="btn btn-delete"
                        qid="delete-s-modal"
                        v-if="isGranted([permissions.SYSTEM_DELETE, permissions.SYSTEM_DELETE_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="value"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group v-if="isMember()">
              <label qid="create-s-users-label">
                {{ $t('systems.create.owners_label') }}
              </label>
              <multiselect
                  v-model="filters.owners"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :multiple="true"
                  :taggable="false"
                  track-by="slug"
                  :placeholder="$t('systems.create.owners_placeholder')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-owners"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-s-tags-label">
                {{ $t('systems.create.tags_label') }}
              </label>
              <multiselect
                  v-model="filters.tags"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="tags"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="true"
                  track-by="name_translated"
                  :placeholder="$t('systems.create.tags_placeholder')"
                  label="name_translated"
                  qid="filter-s-form-tags"
                  @search-change="loadTags"
                  @tag="addTag"
              ></multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-s-third-parties-label">
                {{ $t('systems.create.third_party_label') }}
              </label>
              <multiselect
                  v-model="filters.thirdParties"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="thirdParties"
                  :multiple="true"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :taggable="true"
                  track-by="name"
                  :placeholder="$t('systems.create.third_party_placeholder')"
                  label="name"
                  qid="filter-s-form-third-parties"
                  @search-change="loadThirdParties"
              ></multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="create-s-users-label">
                {{ $t('systems.create.modified_by_label') }}
              </label>
              <multiselect
                  v-model="filters.modifiedBy"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="users"
                  :multiple="false"
                  :taggable="false"
                  track-by="slug"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :placeholder="$t('systems.create.modified_by_placeholder')"
                  label="name"
                  @search-change="loadUsers"
                  qid="filter-s-form-modified-by"
              >
                <template slot="singleLabel" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-from-label">
                {{ $t('systems.create.modified_by_date_from_label') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-from"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedFrom"
                  :clear-button="true"
                  :placeholder="$t('systems.create.modified_by_date_from_placeholder')"
              ></datepicker>
            </b-form-group>

            <b-form-group>
              <label qid="filter-s-modified-to-label">
                {{ $t('systems.create.modified_by_date_to_label') }}
              </label>
              <datepicker
                  class="date-picker"
                  qid="filter-s-form-modified-to"
                  calendar-class="right"
                  @selected="debounceInput"
                  @remove="debounceInput"
                  v-model="filters.modifiedTo"
                  :clear-button="true"
                  :placeholder="$t('systems.create.modified_by_date_to_placeholder')"
              ></datepicker>
            </b-form-group>

            <a href="#" @click="resetFilters" class="text-right" qid="filter-s-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete confirm modal template -->
    <b-modal ref="delete-process" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('systems.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('systems.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('systems.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('systems.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
                  v-if="isGranted([permissions.SYSTEM_DELETE, permissions.SYSTEM_DELETE_OWN], itemToDelete) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('systems.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Download modal template -->
    <b-modal ref="download-modal" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-s-title">
          {{ $t('download_modal.title') }}
        </h4>
      </div>
      <div class="mt-2 mb-2">

        <label class="mt-2">
          {{ $t('download_modal.label.select') }}
        </label>

        <div class="row mt-2 mb-2">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.label.select_all') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="selectAll"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.basic_info') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.basic_info"
              :options="radioOptions"
              qid="basic-info"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.owners') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.owners"
              :options="radioOptions"
              qid="owners"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('download_modal.value.data') }}
        </label>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.data') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.data"
              :options="radioOptions"
              qid="systems"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2">
          {{ $t('systems.tab.controls') }}
        </label>

        <div class="row">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('systems.tab.controls') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.measures"
              :options="radioOptions"
              qid="measures"
            ></b-form-radio-group>
          </div>
        </div>

        <label class="mt-2" v-if="getClient.riskModule">
          {{ $t('download_modal.value.risk_control_module') }}
        </label>

        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_scenarios') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_scenarios"
              :options="radioOptions"
              qid="risk_scenarios"
            ></b-form-radio-group>
          </div>
        </div>
        <div class="row" v-if="getClient.riskModule">
          <div class="col-sm-6">
            <p class="text-normal">
              {{ $t('download_modal.value.risk_validation_log') }}
            </p>
          </div>
          <div class="col-sm-6 text-right">
            <b-form-radio-group
              v-model="downloadForm.risk_validation_log"
              :options="radioOptions"
              qid="risk_validation_log"
            ></b-form-radio-group>
          </div>
        </div>


      </div>
      <div class="text-right">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="closeDownloadModal"
                  qid="delete-s-cancel-button"
        >
          {{ $t('activities.delete.cancel') }}
        </b-button>
        <b-button @click="downloadTable()"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-s-submit-button"
        >
          {{ $t('system.download') }}
        </b-button>
      </div>
    </b-modal>


    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import Avatar from '@/components/Avatar.vue';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import Datepicker from 'vuejs-datepicker';
  import roleCheck from '@/util/ACL/roleCheck.js'
  import SystemEditForm from '@/views/Systems/SystemEditForm.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Systems',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('systems.title'),
            active: true
          }
        ],
        tableItems: [],
        currentPage: 1,
        totalRows: 0,
        itemToDelete: {},
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          process_group: [],
          owners: [],
          tags:[],
          modifiedBy: [],
          modifiedFrom: '',
          modifiedTo: '',
          thirdParties: [],
          parent: 0,
          keywordValue: ''
        },
        tableColumns: {
          responsibles: 1,
          tags: 1,
        },
        processGroups: [],
        typeInput: '',
        loading: true,
        activeProcessGroups: [],
        perPage: '',
        selectedPerPage: "10",
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' },
        ],
        tags: [],
        selectedTags: [],
        users: [],
        selectedUsers: [],
        newTags: [],
        thirdParties: [],
        selectedItem: {},
        showTagsModal: false,
        showUsersModal: false,
        myGuides: [],
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        selectAll: '',
        downloadForm: {
          basic_info: 1,
          owners: 0,
          data: 0,
          risk_scenarios: 0,
          risk_validation_log: 0,
          measures: 0
        },
      }
    },
    components: {
      SystemEditForm,
      Avatar,
      Datepicker,
      ShowMoreModal,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getTableColumns() {
        if (localStorage.getItem('tableColumns')) {
          return JSON.parse(localStorage.getItem('tableColumns'))
        } else {
          return {}
        }
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent()
        if (this.getTableColumns.systems) {
          this.tableColumns = this.getTableColumns.systems
        }
      },
      selectAll: function (value) {
        this.downloadForm.basic_info = value
        this.downloadForm.owners = value
        this.downloadForm.data = value

        if (this.getClient.riskModule) {
          this.downloadForm.risk_scenarios = value
          this.downloadForm.risk_validation_log = value
        }

        this.downloadForm.measures = value
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      goToHome() {
        this.$router.push('/')
      },
      resetFilters() {
        this.filters.owners = [];
        this.filters.tags = [];
        this.filters.modifiedBy = [];
        this.filters.modifiedFrom = '';
        this.filters.modifiedTo = '';
        this.filters.thirdParties= [];
        this.filters.parent = 0
        this.debounceInput();
      },
      handleFilterButton(forceOpened) {
        if (!this.filterOpened) {
          this.loadUsers()
          this.loadTags()
          this.loadThirdParties()
        }
        if (forceOpened) {
          this.filterOpened = true
        } else {
          this.filterOpened = !this.filterOpened
        }
      },
      isMember() {
        if (this.getClient && this.getClient.user_roles && this.getClient.user_roles.length == 1 && this.getClient.user_roles[0].name == 'Member') {
          return false;
        } else {
          return true;
        }
      },
      openTagsModal(object) {
        this.selectedItem = object
        this.showTagsModal = true
      },
      closeTagsModal() {
        this.selectedItem = {}
        this.showTagsModal = false
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleTypeInput(input) {
        this.typeInput = input;
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        let formOwners = [];
        let formTags = [];
        let formThirdParties = [];

        // Append list of users to FormData
        if (this.filters.owners && this.filters.owners.length) {
          for (var i = 0; i < this.filters.owners.length; i++) {
            formOwners[i] = this.filters.owners[i].id;
          }
          this.handleFilterButton(true)
        }

        if (this.filters.tags && this.filters.tags.length) {
          for (var j = 0; j < this.filters.tags.length; j++) {
            formTags[j] = this.filters.tags[j].id;
          }
          this.handleFilterButton(true)
        }

        if (this.filters.thirdParties && this.filters.thirdParties.length) {
          for (var t = 0; t < this.filters.thirdParties.length; t++) {
            formThirdParties[t] = this.filters.thirdParties[t].id;
          }
          this.handleFilterButton(true)
        }

        if (
          (this.filters.modifiedBy && this.filters.modifiedBy.id) ||
          this.filters.modifiedFrom ||
          this.filters.parent ||
          this.filters.modifiedTo
        ) {
          this.handleFilterButton(true)
        }

        piincHttp.get('systems', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            owners: formOwners ? formOwners : undefined,
            tags: formTags ? formTags : undefined,
            thirdParties: formThirdParties ? formThirdParties : undefined,
            modified_by: this.filters.modifiedBy ? this.filters.modifiedBy.id : undefined,
            modified_from: this.filters.modifiedFrom ? this.filters.modifiedFrom : undefined,
            modified_to: this.filters.modifiedTo ? this.filters.modifiedTo : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            parent: this.filters.parent ? this.filters.parent : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;

          // if (!self.tableItems.length && roleCheck('Member')) {
          //   self.goToHome()
          // }
        }, function() {});
      },
      getReducedTags(tags) {
        let reducedTags = []
        if (tags.length > 3) {
          reducedTags = _.cloneDeep(tags);
          reducedTags = reducedTags.slice(0, 3);
        } else {
          reducedTags = tags;
        }
        return reducedTags;
      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)

      },
      handleDownloadModal() {
        this.$refs['download-modal'].show()
      },
      closeDownloadModal() {
        this.$refs['download-modal'].hide()
      },
      downloadTable() {
        let self = this;
        this.downloadForm.client = this.getClient.slug;
        piincHttp.post('systems/download', this.downloadForm).then(function() {
          self.closeDownloadModal();
          self.$toastr('success', self.$t('exports.success_message'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-process'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-process'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('systems/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('systems.delete.success'))
        }, function() {});
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
        }, function() {});
      },
      loadThirdParties(query) {
        let self = this;
        piincHttp.get('third-parties', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.thirdParties = response.data.items
          if (self.thirdParties && !self.thirdParties.length && !query) {
            self.thirdParties.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'System' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'System',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      }
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'systems',
        filters: this.filters
      }
      let columnsObject = {
        type: 'systems',
        tableColumns: this.tableColumns
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storeTableColumns', columnsObject);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.systems) {
          this.filters = this.getFilters.systems
        }
        if (this.getTableColumns.systems) {
          this.tableColumns = this.getTableColumns.systems
        }
        this.loadContent()
      }
      this.loadGuides('system_list_page')
    }
  }
</script>
